import type { Epic } from '../../index.ts'
import { cubePrefetchRequested, cubeStateKey } from '../../Slice/Visualization/VisualizationSlice.ts'
import { EMPTY, catchError, from, groupBy, mergeMap, switchMap } from 'rxjs'
import type { CubeFetchResult } from '../../../Repository/Visualization/Cubes.ts'
import { fetchCube } from '../../../Repository/Visualization/Cubes.ts'
import { getCachedCube, putCachedCube } from '../../../Repository/Visualization/CubeCache.ts'
import { recordCubeDiag } from '../../../Repository/Visualization/CubeDiagnostics.ts'

/**
 * Handles [cubePrefetchRequested] by warming the IndexedDB byte cache for a sibling blast type —
 * and nothing else. Unlike `cubeLoaderEpic` it does NOT register the decoded table in the
 * `CubeTableRegistry` and does NOT dispatch any slice action; the decoded Arrow table is allowed
 * to be GC'd immediately after caching its bytes.
 *
 * Why split from `cubeLoaderEpic`: a prefetch must never consume a resident-table slot, or the
 * speculative siblings would evict the foreground table the visible sink depends on (the registry
 * is bounded at `MAX_RESIDENT_TABLES`). Warming only the byte cache means a later real switch to
 * that blast type revalidates against the cached ETag (→ 304) and decodes locally — fast and
 * network-free — without keeping every blast type resident in memory.
 *
 * `switchMap` per cube key cancels an in-flight prefetch if a newer prefetch for the same key
 * arrives; distinct keys run concurrently. Errors are swallowed — a failed speculative fetch must
 * never surface to the user; the real fetch will retry on demand.
 *
 */
export const cubePrefetchLoaderEpic: Epic = (action$) =>
  action$.pipe(
    mergeMap((a) => (cubePrefetchRequested.match(a) ? [a.payload] : [])),
    groupBy(({ scopeId, blastType }) => cubeStateKey(scopeId, blastType)),
    mergeMap((perKey$) =>
      perKey$.pipe(
        switchMap(({ scopeId, blastType }) =>
          from(getCachedCube(scopeId, blastType)).pipe(
            switchMap((cached) =>
              fetchCube(scopeId, blastType, cached?.etag ?? undefined).pipe(
                mergeMap((res: CubeFetchResult) => {
                  if (res.kind === 'unchanged') return EMPTY // already cached + current — nothing to warm
                  // Warm the byte cache only; the decoded `res.table` is intentionally discarded.
                  recordCubeDiag({
                    kind: 'loaded',
                    key: cubeStateKey(scopeId, blastType),
                    bytes: res.bytes.byteLength,
                    detail: 'prefetch'
                  })
                  return from(
                    putCachedCube(scopeId, blastType, {
                      bytes: res.bytes,
                      etag: res.etag,
                      loadedAt: Date.now()
                    })
                  ).pipe(mergeMap(() => EMPTY))
                }),
                catchError(() => EMPTY)
              )
            )
          )
        )
      )
    )
  )
